import { useEffect, useRef, useState } from "react";

import { DialogoAlerta } from "@/components/alert-dialog";
import { DataTableNoFilter } from "@/components/data-table-no-filter";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useCategorias } from "@/contexts/CategoriasContext";
import { useContaConfig } from "@/contexts/ContaConfigContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { useItem } from "@/contexts/ItemContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import { useSeriais } from "@/contexts/SeriaisContext";
import { useSocket } from "@/contexts/SocketContext";
import { alert } from "@/contexts/AlertModalContext";

import inovaone_api from "@/services/inovaone_api";
import type { ImpressaoItemResponse } from "@/types/Impressao-item-response";
import {
  converterListaZplItemParaUrl,
  formatarData,
} from "@/utils/UtilFuncoes";

import type { ColumnDef } from "@tanstack/react-table";
import {
  ArrowLeft,
  ExternalLink,
  Eye,
  HelpCircle,
  Inbox,
  MapPin,
  Package,
  RefreshCw,
  Scan,
  Search,
  Tag,
  Trash2,
  X,
} from "lucide-react";

function ConferirEtiqueta() {
  const { isLoading, setIsLoading } = useGlobalLoader();
  const { coletaZplPadrao, impressaoConfig } = useImpressaoConfig();
  const { contaConfig } = useContaConfig();
  const { pesquisarItensNaBase, baixarItem } = useItem();
  const { coletaLocalizacoes, localizacoes } = useLocalizacoes();
  const { coletaCategorias, categorias } = useCategorias();
  const { coletaSeriais } = useSeriais();
  const { lastMessage, enviarMensagem } = useSocket();

  const [itemSelecionado, setItemSelecionado] = useState<
    ImpressaoItemResponse | undefined
  >(undefined);
  const [epc, setEpc] = useState<string>("");
  const [itens, setItens] = useState<ImpressaoItemResponse[]>([]);
  const [pagina, setPagina] = useState<number>(0);
  const [totalPaginas, setTotalPaginas] = useState<number>(0);
  const [isOpenAlertDialog, setIsOpenAlertDialog] = useState<boolean>(false);

  // ---- Busca ----
  const pesquisarItens = async (filtro: string = "*") => {
    setIsLoading(true);
    try {
      const response = await inovaone_api.get<ImpressaoItemResponse[]>(
        `/itensPage/${impressaoConfig?.id_conta}/*/*/${filtro}/*/*/*/*/*/*/*/*/*/*/*/${pagina * 10}?limit=10`,
      );
      const data = response.data;
      const response_filter: any = data.pop();
      const total_reg = response_filter?.total_reg || 0;
      const total_pages = Math.ceil(total_reg / 10) || 0;
      setTotalPaginas(total_pages);
      setItens(data);
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  };

  const lerEtiqueta = () => enviarMensagem("LEPR");

  const coletarItem = async (tag: string) => {
    if (!tag) {
      setPagina(0);
      pesquisarItens();
      return;
    }
    tag = tag.trim();
    const item = await pesquisarItensNaBase([tag]);
    if (item && item.length > 0) {
      setItemSelecionado(item[0]);
      setEpc(tag);
    } else {
      pesquisarItens(tag);
    }
  };

  const limparSelecao = () => {
    setItemSelecionado(undefined);
    setEpc("");
    setPagina(0);
  };

  const sincronizar = async () => {
    await coletaCategorias();
    await coletaSeriais();
    await coletaLocalizacoes();
    setPagina(0);
    await pesquisarItens();
    setItemSelecionado(undefined);
    setEpc("");
    alert.success("Dados sincronizados com sucesso!");
  };

  // ---- Effects ----
  // Marca o instante de montagem para ignorar mensagens WS que sobraram
  // de páginas anteriores (lastMessage persiste no contexto do socket).
  const mountTimeRef = useRef<number>(performance.now());

  useEffect(() => {
    if (!lastMessage) return;
    if (lastMessage.timeStamp < mountTimeRef.current) return;
    if (lastMessage.data?.toLowerCase().includes("lepr+")) {
      const tag = lastMessage.data.split("+")[1].trim();
      coletarItem(tag);
    }
  }, [lastMessage]);

  useEffect(() => {
    pesquisarItens(epc || "*");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina]);

  useEffect(() => {
    return () => {
      setItemSelecionado(undefined);
      setEpc("");
    };
  }, []);

  // ---- Colunas (consolidadas) ----
  const baseColumns: ColumnDef<ImpressaoItemResponse>[] = [
    {
      accessorKey: "tag",
      header: "EPC",
      cell: ({ row }) => (
        <span className="text-xs">{row.original.tag}</span>
      ),
    },
    {
      id: "info_adicional_item",
      header: "Informação individual",
      cell: ({ row }) => {
        const { quantidade, valor, dt_validade } = row.original;
        const temValor =
          !!quantidade || (valor && valor > 0) || !!dt_validade;
        if (!temValor)
          return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {quantidade && (
              <Badge variant="secondary" className="font-normal">
                Qtd: {quantidade}
              </Badge>
            )}
            {valor > 0 && (
              <Badge variant="secondary" className="font-normal">
                R$ {valor.toFixed(2)}
              </Badge>
            )}
            {dt_validade && (
              <Badge variant="secondary" className="font-normal">
                Val: {formatarData(dt_validade, false)}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      id: "preview",
      header: "Pré-visualização",
      cell: ({ row }) => {
        const item = row.original;
        const zplPadrao = coletaZplPadrao();
        if (zplPadrao) zplPadrao.zpl = item.zpl_impressao;
        if (!zplPadrao || !zplPadrao.zpl) {
          return (
            <Badge variant="secondary" className="text-xs font-normal">
              ZPL não disponível
            </Badge>
          );
        }
        const url = converterListaZplItemParaUrl(zplPadrao);
        return (
          <HoverCard openDelay={300} closeDelay={50}>
            <HoverCardTrigger asChild>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline cursor-pointer"
              >
                <Eye className="h-3.5 w-3.5" />
                Pré-visualizar
              </a>
            </HoverCardTrigger>
            <HoverCardContent className="w-72 p-2">
              <img
                src={url}
                alt="Pré-visualização"
                className="w-full h-auto bg-white rounded"
              />
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex items-center justify-center gap-1 text-xs text-primary hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                Abrir em nova aba
              </a>
            </HoverCardContent>
          </HoverCard>
        );
      },
    },
  ];

  const columnsListaItens: ColumnDef<ImpressaoItemResponse>[] = [
    ...baseColumns,
    {
      id: "select",
      header: () => <div className="text-right">Ações</div>,
      cell: ({ row }) => (
        <div className="text-right">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setItemSelecionado(row.original);
              setEpc(row.original.tag);
            }}
          >
            <Eye className="h-3.5 w-3.5" />
            Detalhes
          </Button>
        </div>
      ),
    },
  ];

  // ---- Submit do form ----
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPagina(0);
    coletarItem(epc);
  };

  // ---- Helpers de localização ----
  const nomeLoc = (id: string | undefined, nivel: 1 | 2 | 3 | 4): string => {
    if (!id) return "";
    if (nivel === 1) {
      return localizacoes.find((l) => l._id === id)?.descricao || "";
    }
    if (nivel === 2) {
      return (
        localizacoes
          .flatMap((l) => l.children)
          .find((l2) => l2._id === id)?.descricao || ""
      );
    }
    if (nivel === 3) {
      return (
        localizacoes
          .flatMap((l) => l.children)
          .flatMap((l2) => l2.children)
          .find((l3) => l3._id === id)?.descricao || ""
      );
    }
    return (
      localizacoes
        .flatMap((l) => l.children)
        .flatMap((l2) => l2.children)
        .flatMap((l3) => l3.children)
        .find((l4) => l4._id === id)?.descricao || ""
    );
  };

  const camposInfoCompl = [1, 2, 3, 4].map((i) => ({
    idx: i,
    label:
      (contaConfig as any)?.[`info_compl_${i}`] || `Info. Complementar ${i}`,
  }));

  // Backend devolve referências como strings (sem populate). Aceita string
  // ou objeto populado e extrai o _id pra fazer lookup nos contexts locais.
  const extrairId = (ref: unknown): string => {
    if (!ref) return "";
    if (typeof ref === "string") return ref;
    return (ref as any)?._id || "";
  };

  const idCategoriaItem = extrairId(itemSelecionado?.id_categoria);
  const categoriaItem = idCategoriaItem
    ? categorias.find((c) => c._id === idCategoriaItem)
    : undefined;

  const descricaoItem =
    categoriaItem?.descricao ||
    (itemSelecionado?.id_categoria as any)?.descricao ||
    "";

  // Se o item não tem inf_compl_X preenchido, faz fallback pra categoria
  // (cadastrada uma vez) — assim aparece o que foi definido lá.
  const getInfoCompl = (n: number): string => {
    const fromItem = (itemSelecionado as any)?.[`inf_compl_${n}`];
    if (fromItem) return fromItem;
    return (categoriaItem as any)?.[`inf_compl_${n}`] || "";
  };

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <DialogoAlerta
        description="Tem certeza que deseja dar baixa nesta etiqueta? Ela ficará indisponível na base."
        title="Dar baixa na etiqueta"
        isOpen={isOpenAlertDialog}
        onConfirm={() => {
          if (itemSelecionado) baixarItem(itemSelecionado);
          setIsOpenAlertDialog(false);
          setItemSelecionado(undefined);
          setEpc("");
        }}
        onOpenChange={() => setIsOpenAlertDialog(false)}
        key="dialogo-alerta-dar-baixa-etiqueta"
      />

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            Conferir Etiqueta
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Verifique informações de uma etiqueta RFID ou dê baixa em um item
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={sincronizar}
          disabled={isLoading}
        >
          <RefreshCw
            className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
          />
          <span className="hidden sm:inline">Sincronizar</span>
        </Button>
      </div>

      {/* Search Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">
              Buscar etiqueta
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-2"
          >
            <div className="relative flex-1">
              <Tag className="h-4 w-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                disabled={isLoading || itemSelecionado !== undefined}
                id="conferir-etiqueta-tag"
                type="text"
                value={epc}
                onChange={(e) => setEpc(e.target.value)}
                placeholder="Digite ou escaneie o EPC..."
                className="pl-8 pr-8"
                autoFocus
              />
              {epc && !itemSelecionado && (
                <button
                  type="button"
                  onClick={() => setEpc("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Limpar EPC"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="flex gap-2">
              {itemSelecionado ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={limparSelecao}
                  disabled={isLoading}
                  className="flex-1 sm:flex-initial"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Voltar
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 sm:flex-initial"
                >
                  <Search className="h-4 w-4" />
                  Buscar
                </Button>
              )}

              {!itemSelecionado && (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={lerEtiqueta}
                        disabled={isLoading}
                        className="flex-1 sm:flex-initial"
                      >
                        <Scan className="h-4 w-4" />
                        Ler com módulo
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <div className="flex items-start gap-2">
                        <HelpCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <p>
                          Aciona o leitor RFID conectado. A primeira tag
                          detectada preenche o campo automaticamente e busca
                          os dados na base.
                        </p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Conteúdo: ou lista, ou detalhe */}
      {!itemSelecionado ? (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Inbox className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">
                  Itens na base
                </CardTitle>
              </div>
              <Badge variant="secondary" className="font-normal">
                {itens.length} de {totalPaginas * 10}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {itens.length === 0 && !isLoading ? (
              <div className="py-12 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                <Inbox className="h-8 w-8 opacity-40" />
                <p className="text-sm">Nenhum item encontrado.</p>
                {epc && (
                  <p className="text-xs">
                    Nenhum resultado para{" "}
                    <span className="">{epc}</span>
                  </p>
                )}
              </div>
            ) : (
              <DataTableNoFilter
                columns={columnsListaItens}
                data={itens}
                onNextPage={() => {
                  if (pagina < totalPaginas - 1) setPagina(pagina + 1);
                }}
                onPreviousPage={() => {
                  if (pagina > 0) setPagina(pagina - 1);
                }}
                onPageChange={(p) => setPagina(p)}
                page={pagina}
                totalPages={totalPaginas}
              />
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Resumo da seleção + ações */}
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15">
                  <Tag className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    Etiqueta selecionada
                  </p>
                  <p className="text-sm font-medium truncate">
                    {itemSelecionado.tag}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={limparSelecao}
                  disabled={isLoading}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Voltar para lista
                </Button>
                {itemSelecionado._id && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setIsOpenAlertDialog(true)}
                    disabled={isLoading}
                  >
                    <Trash2 className="h-4 w-4" />
                    Dar baixa
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Localizações */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">
                  Localizações
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[1, 2, 3, 4].map((nivel) => {
                  const ref =
                    nivel === 1
                      ? itemSelecionado?.id_nivel_loc_1
                      : nivel === 2
                        ? itemSelecionado?.id_nivel_loc_2
                        : nivel === 3
                          ? itemSelecionado?.id_nivel_loc_3
                          : itemSelecionado?.id_nivel_loc_4;
                  const id = extrairId(ref);
                  const valor = nomeLoc(id, nivel as 1 | 2 | 3 | 4);
                  return (
                    <div key={nivel} className="grid gap-2">
                      <Label
                        htmlFor={`conferir-etiqueta-loc-${nivel}`}
                        className="text-xs text-muted-foreground"
                      >
                        Nível {nivel}
                      </Label>
                      <Input
                        disabled
                        id={`conferir-etiqueta-loc-${nivel}`}
                        type="text"
                        value={valor || "—"}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Item */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">
                  Dados do item
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2">
                <Label
                  htmlFor="conferir-etiqueta-descricao"
                  className="text-xs text-muted-foreground"
                >
                  Descrição
                </Label>
                <Input
                  disabled
                  id="conferir-etiqueta-descricao"
                  type="text"
                  value={descricaoItem || "—"}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {camposInfoCompl.map((c) => (
                  <div key={c.idx} className="grid gap-2">
                    <Label
                      htmlFor={`conferir-etiqueta-info-${c.idx}`}
                      className="text-xs text-muted-foreground"
                    >
                      {c.label}
                    </Label>
                    <Input
                      disabled
                      id={`conferir-etiqueta-info-${c.idx}`}
                      type="text"
                      value={getInfoCompl(c.idx) || "—"}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Etiqueta (preview + info individual) */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">
                  Etiqueta
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-center">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    EPC
                  </p>
                  <p className="text-sm truncate">
                    {itemSelecionado.tag}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Informação individual
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {itemSelecionado.quantidade && (
                      <Badge variant="secondary" className="font-normal">
                        Qtd: {itemSelecionado.quantidade}
                      </Badge>
                    )}
                    {itemSelecionado.valor > 0 && (
                      <Badge variant="secondary" className="font-normal">
                        R$ {itemSelecionado.valor.toFixed(2)}
                      </Badge>
                    )}
                    {itemSelecionado.dt_validade && (
                      <Badge variant="secondary" className="font-normal">
                        Val: {formatarData(itemSelecionado.dt_validade, false)}
                      </Badge>
                    )}
                    {!itemSelecionado.quantidade &&
                      !itemSelecionado.valor &&
                      !itemSelecionado.dt_validade && (
                        <span className="text-xs text-muted-foreground">
                          —
                        </span>
                      )}
                  </div>
                </div>
                <div className="sm:justify-self-end">
                  {(() => {
                    const zplPadrao = coletaZplPadrao();
                    if (zplPadrao)
                      zplPadrao.zpl = itemSelecionado.zpl_impressao;
                    if (!zplPadrao || !zplPadrao.zpl) {
                      return (
                        <Badge
                          variant="secondary"
                          className="text-xs font-normal"
                        >
                          ZPL não disponível
                        </Badge>
                      );
                    }
                    const url = converterListaZplItemParaUrl(zplPadrao);
                    return (
                      <HoverCard openDelay={300} closeDelay={50}>
                        <HoverCardTrigger asChild>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline cursor-pointer"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            Pré-visualizar
                          </a>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-72 p-2">
                          <img
                            src={url}
                            alt="Pré-visualização"
                            className="w-full h-auto bg-white rounded"
                          />
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 flex items-center justify-center gap-1 text-xs text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Abrir em nova aba
                          </a>
                        </HoverCardContent>
                      </HoverCard>
                    );
                  })()}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export default ConferirEtiqueta;
